import { useTranslation } from "react-i18next";
import type { Filter, SavedView } from "@/components/FilterBar";

type Preset = { key: string; filters: Filter[] };
const APPT: Preset[] = [
  { key: "vip", filters: [{ field: "customer_vip", op: "eq", value: true }] },
  { key: "unsettled", filters: [{ field: "settlement", op: "in", value: ["unsettled"] }] },
  { key: "noShows", filters: [{ field: "status", op: "in", value: ["no_show"] }] },
  { key: "overCapacity", filters: [{ field: "conflict", op: "eq", value: true }] },
  { key: "online", filters: [{ field: "source", op: "in", value: ["online"] }] },
];
const PRESETS: Record<string, Preset[]> = {
  customers: [
    { key: "vip", filters: [{ field: "vip", op: "eq", value: true }] },
    { key: "blocked", filters: [{ field: "status", op: "in", value: ["blocked"] }] },
    { key: "noShows", filters: [{ field: "no_show_count", op: "gte", value: 1 }] },
    { key: "newCustomers", filters: [{ field: "status", op: "in", value: ["guest", "new"] }] },
  ],
  appointments: APPT,
  calendar: APPT,
};
const same = (a: Filter, b: Filter) => a.field === b.field && a.op === b.op && JSON.stringify(a.value) === JSON.stringify(b.value);

/** One-click filter pills: fixed presets per entity plus the pinned saved views. A pill is "on" when every one of its
 * filters is applied; clicking it again removes exactly those filters. */
export function QuickPills({ entity, filters, onChange, views, onView }: { entity: string; filters: Filter[]; onChange: (f: Filter[]) => void; views: SavedView[]; onView: (v: SavedView) => void }) {
  const { t } = useTranslation();
  const presets = PRESETS[entity] ?? [];
  const pinned = views.filter((v) => v.pinned);
  if (!presets.length && !pinned.length) return null;
  const active = (fs: Filter[]) => fs.length > 0 && fs.every((f) => filters.some((x) => same(x, f)));
  const toggle = (fs: Filter[]) => {
    if (active(fs)) onChange(filters.filter((x) => !fs.some((f) => same(x, f))));
    else onChange([...filters.filter((x) => !fs.some((f) => f.field === x.field)), ...fs]);
  };
  const cls = (on: boolean) => `rounded-full border px-2.5 py-0.5 text-xs transition ${on ? "border-brand bg-brand text-white" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"}`;
  return (
    <div className="flex flex-wrap gap-1">
      {presets.map((p) => <button key={p.key} type="button" className={cls(active(p.filters))} onClick={() => toggle(p.filters)}>{t(`pills.${p.key}`)}</button>)}
      {pinned.map((v) => <button key={v.id} type="button" className={cls(active(v.filters))} title={t("views.pinnedView")} onClick={() => (active(v.filters) ? toggle(v.filters) : onView(v))}>📌 {v.name}</button>)}
    </div>
  );
}
